export function shortProposalURL(value, base) {
  const url = new URL(value, base);
  const proposal = url.pathname.match(/\/proposals\/(\d+)$/);
  if (proposal && url.origin === new URL(base).origin) url.pathname = `/proposals/${proposal[1]}`;
  return url.href;
}

export async function copyLink(url, doc = document) {
  try {
    await doc.defaultView.navigator.clipboard.writeText(url);
  } catch {
    const field = doc.createElement("textarea");
    field.value = url;
    field.readOnly = true;
    field.style.position = "fixed";
    field.style.opacity = "0";
    doc.body.append(field);
    field.select();
    try {
      if (!doc.execCommand("copy")) throw new Error("Copy failed");
    } finally {
      field.remove();
    }
  }
}

export class NytieCommentUploads {
  constructor(element) {
    this.element = element;
    this.form = element.closest("form");
    this.input = element.querySelector('input[type="file"]');
    this.previews = element.querySelector("[data-image-previews]");
    this.status = element.querySelector("[data-image-status]");
    this.pending = 0;
    this.input.addEventListener("change", () => this.upload(Array.from(this.input.files)));
    this.form.addEventListener("submit", (event) => {
      if (!this.pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.status.textContent = element.dataset.pending;
    }, true);
    this.form.addEventListener("ajax:success", () => {
      if (!this.form.querySelector("textarea").value) this.previews.replaceChildren();
    });
  }

  async upload(files) {
    this.status.textContent = "";
    this.input.disabled = true;
    this.pending++;
    try {
      if (this.previews.childElementCount + files.length > 4) throw new Error(this.element.dataset.failed);
      for (const file of files) {
        if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type) || file.size > 5 * 1024 * 1024) throw new Error(this.element.dataset.failed);
        const data = new this.form.ownerDocument.defaultView.FormData();
        data.append("image", file);
        data.append("commentable_gid", this.form.elements["comment[commentable_gid]"].value);
        const token = this.form.ownerDocument.querySelector('meta[name="csrf-token"]')?.content;
        const response = await fetch(this.element.dataset.uploadUrl, {
          method: "POST", body: data,
          headers: token ? { "X-CSRF-Token": token } : {}
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || this.element.dataset.failed);
        const row = this.form.ownerDocument.createElement("div");
        const image = this.form.ownerDocument.createElement("img");
        image.src = result.url;
        image.alt = file.name;
        const hidden = this.form.ownerDocument.createElement("input");
        hidden.type = "hidden";
        hidden.name = "comment[image_ids][]";
        hidden.value = result.signed_id;
        const remove = this.form.ownerDocument.createElement("button");
        remove.type = "button";
        remove.textContent = this.element.dataset.remove;
        remove.addEventListener("click", () => row.remove());
        row.append(image, hidden, remove);
        this.previews.append(row);
      }
    } catch (error) {
      this.status.textContent = error.message || this.element.dataset.failed;
    } finally {
      this.pending--;
      if (!this.pending && this.status.textContent === this.element.dataset.pending) this.status.textContent = "";
      this.input.disabled = false;
      this.input.value = "";
      this.form.querySelector("textarea").dispatchEvent(new this.form.ownerDocument.defaultView.Event("input", { bubbles: true }));
    }
  }
}

export function initializeDiscussion(doc = document) {
  if (doc.documentElement.dataset.nytieDiscussion) return;
  doc.documentElement.dataset.nytieDiscussion = "true";
  let toastTimer;
  const showCopyToast = (message) => {
    let toast = doc.querySelector("[data-nytie-copy-toast]");
    if (!toast) {
      toast = doc.createElement("div");
      toast.dataset.nytieCopyToast = "true";
      toast.setAttribute("role", "status");
      doc.body.append(toast);
    }
    doc.defaultView.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = doc.defaultView.setTimeout(() => { toast.hidden = true; }, 2500);
  };
  const initialize = () => {
    doc.querySelectorAll("[data-nytie-comment-upload]:not([data-initialized])").forEach((element) => {
      element.dataset.initialized = "true";
      new NytieCommentUploads(element);
    });
    doc.querySelectorAll('.comment .dropdown__button[href*="commentId="]:not([data-nytie-copy])').forEach((link) => {
      link.dataset.nytieCopy = shortProposalURL(link.href, doc.location.href);
      link.removeAttribute("target");
      link.removeAttribute("data-external-link");
      link.querySelectorAll(".sr-only").forEach((note) => note.remove());
      link.setAttribute("role", "button");
    });
    doc.querySelectorAll("[data-nytie-native-share]").forEach((button) => {
      button.hidden = !(doc.defaultView.navigator.share && doc.defaultView.matchMedia("(max-width: 767px)").matches);
    });
  };
  doc.addEventListener("click", async (event) => {
    const desktopShare = !doc.defaultView.matchMedia("(max-width: 767px)").matches && event.target.closest('[data-dialog-open="socialShare"]');
    const button = desktopShare || event.target.closest("[data-nytie-copy], [data-nytie-native-share]");
    if (!button) return;
    const widget = desktopShare ? doc.querySelector("#socialShare [data-nytie-share-url]") : button.closest("[data-nytie-share-url]");
    if (desktopShare && !widget) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const status = widget?.querySelector("[data-nytie-share-status]") || doc.querySelector("[data-nytie-share-status]");
    const url = widget ? new URL(widget.dataset.nytieShareUrl, doc.location.href).href : button.dataset.nytieCopy;
    try {
      if (button.hasAttribute("data-nytie-native-share")) {
        await doc.defaultView.navigator.share({ url, title: doc.querySelector("h1")?.textContent.trim() });
      } else {
        await copyLink(url, doc);
        if (desktopShare) showCopyToast(status.dataset.copiedToast);
        if (status) status.textContent = status.dataset.copied;
        if (!widget) {
          const label = button.querySelector("span");
          if (label && status) label.textContent = status.dataset.copied;
          button.setAttribute("aria-live", "polite");
        }
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      if (desktopShare) doc.defaultView.Decidim?.currentDialogs?.socialShare?.open();
      if (status) status.textContent = status.dataset.failed;
      const field = widget?.querySelector("input[readonly]") || doc.createElement("input");
      field.value = url;
      field.readOnly = true;
      if (!field.isConnected) button.after(field);
      field.focus();
      field.select();
    }
  }, true);
  initialize();
  const observer = new doc.defaultView.MutationObserver(initialize);
  observer.observe(doc.documentElement, { childList: true, subtree: true });
  return observer;
}
